import {
  mirrorCenterX,
  mirrorAnchorX,
  type Rect,
} from "@yjsgames/phaser-ui/panel-content";
import type { TextDirection } from "@yjsgames/i18n";
import { computeLabelButtonRow } from "./tabRowLayout.js";
import spec from "../data/layouts/relic.json";

/** 제목 아래 팁 캐러셀 영역 — 높이 40, 위 간격 6 (스탯/합성 탭 동일). 카드 그리드가 그만큼 아래로 밀린다. */
const TIP_H = 40;
const TIP_GAP = 6;

export interface RelicCardLayout {
  /** 카드 배경 rect (origin 0,0). */
  readonly bg: Rect;
  /** 카드 내부 유물 아이콘 중심 좌표/표시 크기. */
  readonly icon: { x: number; y: number; size: number };
  /** 카드 내부 텍스트 x (RTL이면 우측 패딩). */
  readonly textX: number;
  readonly nameY: number;
  readonly statusY: number;
}

export interface RelicGridLayout {
  readonly textOriginX: number;
  /** 카드 내부 텍스트 origin x (LTR 0 / RTL 1). */
  readonly cardTextOriginX: number;
  readonly title: { x: number; y: number };
  /** 제목 아래 팁 캐러셀 영역 (전체폭) — TipCarouselView 배치 기준(스탯 탭 동형). */
  readonly tip: Rect;
  readonly cards: readonly RelicCardLayout[];
}

/**
 * 유물 탭 2열 카드 그리드 좌표 — grid 수치(열·간격·카드크기·내부 offset)는 relic.json 선언,
 * RTL 열 미러·카드 내부 패딩 미러는 기존 mirror 유틸(컬럼 순서/텍스트 좌우 반전) 유지.
 */
export function computeRelicGridLayout(
  content: Rect,
  dir: TextDirection,
  count: number,
): RelicGridLayout {
  const head = computeLabelButtonRow(content.x, content.width, 0, dir);
  const cardW = Math.floor((content.width - spec.cardGap) / spec.cols);
  // 팁 캐러셀은 기존 그리드 시작 위치(gridTop)에 두고, 카드는 (TIP_H+TIP_GAP)만큼 아래로 민다.
  const tipY = content.y + spec.gridTop;
  const gridTop = tipY + TIP_H + TIP_GAP;
  const cards: RelicCardLayout[] = [];
  for (let i = 0; i < count; i++) {
    const col = i % spec.cols;
    const row = Math.floor(i / spec.cols);
    const mcol = dir === "rtl" ? spec.cols - 1 - col : col;
    const cx = content.x + mcol * (cardW + spec.cardGap);
    const cy = gridTop + row * (spec.cardH + spec.cardVGap);
    const iconCenterInset = spec.cardPadX + spec.iconSize / 2;
    const textInset = spec.cardPadX + spec.iconSize + spec.iconGap;
    cards.push({
      bg: { x: cx, y: cy, width: cardW, height: spec.cardH },
      icon: {
        x: cx + mirrorCenterX(iconCenterInset, cardW, dir),
        y: cy + spec.cardH / 2,
        size: spec.iconSize,
      },
      textX: cx + mirrorCenterX(textInset, cardW, dir),
      nameY: cy + spec.nameDy,
      statusY: cy + spec.statusDy,
    });
  }
  return {
    textOriginX: head.textOriginX,
    cardTextOriginX: mirrorAnchorX(0, dir),
    title: { x: head.textX, y: content.y + spec.title },
    tip: { x: content.x, y: tipY, width: content.width, height: TIP_H },
    cards,
  };
}
